import { ArrowTopRightOnSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useStickyNoteStore } from '@/shared/stores/sticky-note-store';
import { Button, Dialog, Text } from '@/shared/ui';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * 付箋一覧ダイアログ
 * 全付箋をリスト表示し、クリックで該当ページに遷移+スクロール位置復元
 */
export const StickyNoteListDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const notes = useStickyNoteStore((s) => s.notes);
  const removeNote = useStickyNoteStore((s) => s.removeNote);
  const navigate = useNavigate();

  const handleNavigate = ({
    url,
    scrollX,
    scrollY,
  }: {
    url: string;
    scrollX: number;
    scrollY: number;
  }) => {
    onOpenChange(false);

    if (window.location.pathname === url) {
      window.scrollTo(scrollX, scrollY);
    } else {
      // TanStack Router で遷移後、スクロール復元
      navigate({ to: url }).then(() => {
        requestAnimationFrame(() => {
          window.scrollTo(scrollX, scrollY);
        });
      });
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('debug-menu.sticky-note.list-title')}
      hideConfirmButton
      hideCancelButton
      width="l"
    >
      {notes.length === 0 ? (
        <Text>{t('debug-menu.sticky-note.no-notes')}</Text>
      ) : (
        <div className="max-h-96 space-y-2 overflow-y-auto">
          {notes.map((note) => (
            <div
              key={note.id}
              className="flex items-start justify-between gap-2 rounded-md border border-stone-200 p-3"
            >
              <div className="min-w-0 flex-1">
                <Text variant="description">{note.url}</Text>
                <Text>{note.content || '---'}</Text>
                <Text variant="description">
                  {new Date(note.createdAt).toLocaleString('ja-JP')}
                </Text>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    handleNavigate({ url: note.url, scrollX: note.scrollX, scrollY: note.scrollY })
                  }
                  aria-label={t('debug-menu.sticky-note.navigate')}
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeNote(note.id)}
                  aria-label={t('debug-menu.sticky-note.delete')}
                >
                  <TrashIcon className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
};
